import { type ErrorInfo } from 'react';
import { AuthService } from '../AuthService';
import { type IBugReportData } from '@/types/IBugReportData';
import { type IBugReportUserData } from '@/types/IBugReportUserData';

export type ErrorBoundaryScope = 'global' | 'page';

/**
 * Краткое имя/версия браузера для поля `browser`.
 */
const getBrowserInfo = (): string => {
  if (typeof navigator === 'undefined') return 'N/A';
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg'))
    return `Chrome ${ua.match(/Chrome\/(\d+)/)?.[1] ?? '?'}`;
  if (ua.includes('Firefox'))
    return `Firefox ${ua.match(/Firefox\/(\d+)/)?.[1] ?? '?'}`;
  if (ua.includes('Safari') && !ua.includes('Chrome'))
    return `Safari ${ua.match(/Version\/(\d+)/)?.[1] ?? '?'}`;
  if (ua.includes('Edg')) return `Edge ${ua.match(/Edg\/(\d+)/)?.[1] ?? '?'}`;
  return 'Unknown';
};

const getOsInfo = (): string => {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
};

/**
 * Данные сотрудника, при котором произошла ошибка (требование тимлида).
 * Берём из AuthService.user; если пользователь не авторизован — пустой объект.
 */
const collectUserData = (): IBugReportUserData => {
  const user = AuthService.user;
  if (user == null) return {};

  return {
    account_id: user.id,
    email: user.email,
    name: user.name,
    surname: user.surname,
    roles: user.roles
  };
};

/**
 * Собирает тело bug-report из пойманной ErrorBoundary ошибки.
 *
 * Все ошибки админ-панели помечаются типом `ADMIN_PANEL` — по нему команда
 * разработки отличает отчёты из админки от клиентского приложения.
 */
export const collectErrorData = (
  error: Error,
  errorInfo: ErrorInfo | null,
  scope: ErrorBoundaryScope
): IBugReportData => {
  const now = new Date();
  const readableTime = now.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : 'N/A';
  const browser = getBrowserInfo();
  const userData = collectUserData();
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A';

  // Полный отчёт — уходит в `data` как читаемый JSON (бэкенд покажет в деталях).
  const fullReport = {
    source: 'admin-panel',
    scope,
    error: {
      message: error.message,
      name: error.name,
      time: readableTime,
      timestamp: now.toISOString(),
      path: pathname
    },
    stack: {
      error: error.stack ?? null,
      react: errorInfo?.componentStack ?? null
    },
    system: {
      browser,
      os: getOsInfo(),
      userAgent
    },
    user: userData
  };

  // Компактная строка для быстрого просмотра в списке отчётов.
  const userLine =
    userData.account_id != null
      ? `id=${userData.account_id} ${userData.email ?? ''}`.trim()
      : 'no user';
  const context = [
    error.message,
    `[ADMIN_PANEL/${scope}]`,
    readableTime,
    pathname,
    browser,
    userLine
  ].join(' | ');

  return {
    data: JSON.stringify(fullReport, null, 2),
    local_storage: 'N/A',
    device: userAgent,
    context,
    browser,
    url: pathname,
    user_data: JSON.stringify(userData)
  };
};
